/**
 * components/BulkUpdatePanel.tsx
 *
 * Fleet-wide one-shot editor for `search_terms` and `max_pages`
 * runtime-config overrides. Sits above the scraper grid on the Manage
 * Scrapers page and lets ops change keywords + page-count across the
 * whole fleet without clicking through each ScraperCard individually.
 *
 * Safety rails (see PR description for the why):
 *  - Confirmation dialog before submit, showing exact scope + payload
 *  - For scope=all & N>5 the operator must type "APPLY" to enable submit
 *  - Blank inputs do NOT silently clear — operator opts in via the
 *    "Clear instead of set" checkbox, which sends `null` for the
 *    selected field
 *  - base_url is deliberately not editable here (each scraper has a
 *    unique target URL)
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  bulkUpdateRuntimeConfig,
  type BulkScope,
  type BulkRuntimeConfigResult,
} from '@/api/observe';
import type { ScraperRecord } from '@/types';
import { cn } from '@/lib/cn';

type ScopePreset = 'enabled' | 'all' | 'cf_workers' | 'docker' | 'custom';

const SCOPE_PRESETS: Array<{ key: ScopePreset; label: string; hint: string }> = [
  { key: 'enabled',    label: 'Enabled',       hint: 'Only currently-active scrapers' },
  { key: 'all',        label: 'All',           hint: 'Every scraper, enabled or not'  },
  { key: 'cf_workers', label: 'cf_workers',    hint: 'Cloudflare-worker executors'    },
  { key: 'docker',     label: 'docker',        hint: 'Docker-container executors'     },
  { key: 'custom',     label: 'Custom…',       hint: 'Pick specific scrapers by id'   },
];

interface Props {
  scrapers: ScraperRecord[];
}

export default function BulkUpdatePanel({ scrapers }: Props) {
  const qc = useQueryClient();

  // Collapsed by default — most page-views aren't editing config, and
  // the panel takes a lot of vertical space when expanded.
  const [open, setOpen] = useState(false);

  // Inputs. Strings (not parsed values) so we can distinguish "" from
  // a number; parsing happens at submit time.
  const [termsRaw,   setTermsRaw]   = useState('');
  const [maxPagesRaw,setMaxPagesRaw]= useState('');
  const [clearMode,  setClearMode]  = useState(false);
  // Which fields the operator is actively editing. Both default off so
  // an accidental submit-with-everything-blank can't escape — at least
  // one must be ticked AND have content (or be in clearMode).
  const [editTerms,  setEditTerms]  = useState(false);
  const [editMaxPages, setEditMaxPages] = useState(false);

  const [scopePreset, setScopePreset] = useState<ScopePreset>('enabled');
  const [customIds,   setCustomIds]   = useState<Set<string>>(new Set());

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applyTyped,  setApplyTyped]  = useState('');

  const [result, setResult] = useState<BulkRuntimeConfigResult | null>(null);

  // Resolve scope → ids client-side so we can show "Will apply to N
  // scrapers". The server re-resolves authoritatively at submit time,
  // so a stale client-side count is just a UI hint, not a contract.
  const resolvedIds = useMemo(() => {
    switch (scopePreset) {
      case 'all':        return scrapers.map((s) => s.id);
      case 'enabled':    return scrapers.filter((s) => s.enabled).map((s) => s.id);
      case 'cf_workers': return scrapers.filter((s) => s.executor === 'cf_workers').map((s) => s.id);
      case 'docker':     return scrapers.filter((s) => s.executor === 'docker').map((s) => s.id);
      case 'custom':     return Array.from(customIds);
    }
  }, [scopePreset, scrapers, customIds]);

  const parsedTerms = useMemo(() => {
    return termsRaw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [termsRaw]);
  const parsedMaxPages = useMemo(() => {
    const n = Number(maxPagesRaw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }, [maxPagesRaw]);

  // Enablement rules:
  //  - at least one field must be active (editTerms || editMaxPages)
  //  - if not in clearMode, the active field(s) must have content
  //  - scope must resolve to ≥1 scraper
  //  - the typo-protected APPLY check (scope=all & N>5) must pass
  const fieldsValid =
    (editTerms || editMaxPages) &&
    (clearMode || (
      (!editTerms    || parsedTerms.length > 0) &&
      (!editMaxPages || parsedMaxPages !== null)
    ));
  const needsApplyTyping = scopePreset === 'all' && resolvedIds.length > 5;
  const applyOk = !needsApplyTyping || applyTyped === 'APPLY';
  const canSubmit = fieldsValid && resolvedIds.length > 0;

  const mutation = useMutation({
    mutationFn: bulkUpdateRuntimeConfig,
    onSuccess: (res) => {
      setResult(res);
      setConfirmOpen(false);
      setApplyTyped('');
      // Refetch the scraper list so each card shows its new override.
      qc.invalidateQueries({ queryKey: ['scrapers'] });
    },
  });

  function buildPayload() {
    const scope: BulkScope =
      scopePreset === 'custom' ? { ids: Array.from(customIds) } : scopePreset;
    const payload: { search_terms?: string[] | null; max_pages?: number | null; scope: BulkScope } = { scope };
    if (editTerms) {
      payload.search_terms = clearMode ? null : parsedTerms;
    }
    if (editMaxPages) {
      payload.max_pages = clearMode ? null : parsedMaxPages;
    }
    return payload;
  }

  function onClickApply() {
    if (!canSubmit) return;
    setConfirmOpen(true);
  }

  function onConfirm() {
    if (!applyOk) return;
    mutation.mutate(buildPayload());
  }

  function resetForm() {
    setTermsRaw('');
    setMaxPagesRaw('');
    setEditTerms(false);
    setEditMaxPages(false);
    setClearMode(false);
    setResult(null);
  }

  return (
    <div className="border border-slate-800 bg-slate-900/40 rounded-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-900/60"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="font-medium">Bulk update — apply to multiple scrapers</span>
        <span className="text-xs text-slate-500 ml-auto">
          search_terms · max_pages · scope-filtered
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-800">
          {/* Field 1: keywords */}
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={editTerms}
                onChange={(e) => setEditTerms(e.target.checked)}
                className="accent-emerald-500"
              />
              <span>Set keywords (comma-separated)</span>
            </label>
            <textarea
              disabled={!editTerms || clearMode}
              rows={2}
              value={termsRaw}
              onChange={(e) => setTermsRaw(e.target.value)}
              placeholder="ps5, xbox series x, switch oled"
              className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-100 disabled:opacity-40"
            />
            {editTerms && !clearMode && (
              <div className="text-[11px] text-slate-500">
                {parsedTerms.length} term{parsedTerms.length === 1 ? '' : 's'} parsed
              </div>
            )}
          </div>

          {/* Field 2: max_pages */}
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={editMaxPages}
                onChange={(e) => setEditMaxPages(e.target.checked)}
                className="accent-emerald-500"
              />
              <span>Set max pages per term</span>
            </label>
            <input
              type="number"
              min={1}
              max={100}
              disabled={!editMaxPages || clearMode}
              value={maxPagesRaw}
              onChange={(e) => setMaxPagesRaw(e.target.value)}
              placeholder="3"
              className="w-32 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-100 disabled:opacity-40"
            />
          </div>

          {/* Clear-instead-of-set toggle. The blast-radius safety
              rail — operators must opt in to "clear" rather than
              having an empty input silently wipe overrides. */}
          <label className="flex items-center gap-2 text-xs text-amber-300">
            <input
              type="checkbox"
              checked={clearMode}
              onChange={(e) => setClearMode(e.target.checked)}
              className="accent-amber-500"
            />
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Clear instead of set (sends null for ticked fields → falls back to env / defaults)</span>
          </label>

          {/* Scope picker */}
          <div className="space-y-1.5">
            <div className="text-xs text-slate-400">Scope</div>
            <div className="flex flex-wrap gap-1">
              {SCOPE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setScopePreset(p.key)}
                  title={p.hint}
                  className={cn(
                    'px-2.5 py-1 text-xs rounded border',
                    scopePreset === p.key
                      ? 'bg-slate-800 border-slate-600 text-white'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {scopePreset === 'custom' && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-slate-800 rounded p-2 space-y-1 bg-slate-950/60">
                {scrapers.length === 0 && (
                  <div className="text-xs text-slate-500">No scrapers registered.</div>
                )}
                {scrapers.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={customIds.has(s.id)}
                      onChange={(e) => {
                        const next = new Set(customIds);
                        if (e.target.checked) next.add(s.id); else next.delete(s.id);
                        setCustomIds(next);
                      }}
                      className="accent-emerald-500"
                    />
                    <code className="text-slate-200">{s.id}</code>
                    <span className="text-slate-500">— {s.display_name}</span>
                    {!s.enabled && (
                      <span className="text-[10px] text-slate-600 uppercase">paused</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Live preview */}
          <div className="text-xs text-slate-400">
            Will apply to <span className="text-white font-medium">{resolvedIds.length}</span>{' '}
            scraper{resolvedIds.length === 1 ? '' : 's'}
            {resolvedIds.length > 0 && (
              <>
                : <span className="text-slate-300">
                  {resolvedIds.slice(0, 8).join(', ')}
                  {resolvedIds.length > 8 && ` … (+${resolvedIds.length - 8})`}
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={onClickApply}
              className={cn(
                'px-3 py-1.5 text-sm rounded font-medium',
                canSubmit
                  ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                  : 'bg-slate-800 text-slate-500 cursor-not-allowed',
              )}
            >
              {clearMode ? 'Clear on' : 'Apply to'} {resolvedIds.length} scraper{resolvedIds.length === 1 ? '' : 's'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-sm rounded text-slate-400 hover:text-white"
            >
              Reset
            </button>
          </div>

          {result && (
            <ResultPanel result={result} onDismiss={() => setResult(null)} />
          )}
        </div>
      )}

      {confirmOpen && (
        <ConfirmDialog
          payload={buildPayload()}
          ids={resolvedIds}
          needsApplyTyping={needsApplyTyping}
          applyTyped={applyTyped}
          onApplyTypedChange={setApplyTyped}
          submitting={mutation.isPending}
          error={mutation.error as Error | null}
          applyOk={applyOk}
          onCancel={() => { setConfirmOpen(false); setApplyTyped(''); }}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
}

function ConfirmDialog({
  payload, ids, needsApplyTyping, applyTyped, onApplyTypedChange,
  submitting, error, applyOk, onCancel, onConfirm,
}: {
  payload:           ReturnType<typeof Object>;
  ids:               string[];
  needsApplyTyping:  boolean;
  applyTyped:        string;
  onApplyTypedChange:(s: string) => void;
  submitting:        boolean;
  error:             Error | null;
  applyOk:           boolean;
  onCancel:          () => void;
  onConfirm:         () => void;
}) {
  const p = payload as { search_terms?: string[] | null; max_pages?: number | null; scope: unknown };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-md p-4 space-y-3">
        <div className="flex items-center gap-2 text-amber-300">
          <AlertTriangle className="w-5 h-5" />
          <h2 className="text-sm font-medium text-white">Confirm bulk update</h2>
        </div>

        <div className="text-xs text-slate-300 space-y-1">
          <div>
            Target: <span className="text-white font-medium">{ids.length}</span> scraper{ids.length === 1 ? '' : 's'}
          </div>
          <div className="text-slate-400 break-all">
            {ids.slice(0, 10).join(', ')}
            {ids.length > 10 && ` … (+${ids.length - 10})`}
          </div>
        </div>

        <pre className="text-[11px] bg-slate-950 border border-slate-800 rounded p-2 max-h-60 overflow-auto text-slate-200">
{JSON.stringify({
  search_terms: 'search_terms' in p ? p.search_terms : '(unchanged)',
  max_pages:    'max_pages'    in p ? p.max_pages    : '(unchanged)',
}, null, 2)}
        </pre>

        {needsApplyTyping && (
          <div className="space-y-1">
            <div className="text-xs text-amber-300">
              Touching &gt;5 scrapers with scope=all. Type <code className="text-white">APPLY</code> to enable.
            </div>
            <input
              type="text"
              value={applyTyped}
              onChange={(e) => onApplyTypedChange(e.target.value)}
              placeholder="APPLY"
              className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-slate-100"
            />
          </div>
        )}

        {error && (
          <div className="text-xs text-red-300">Error: {error.message}</div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm rounded text-slate-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || !applyOk}
            className={cn(
              'px-3 py-1.5 text-sm rounded font-medium',
              applyOk && !submitting
                ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed',
            )}
          >
            {submitting ? 'Applying…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultPanel({
  result, onDismiss,
}: {
  result: BulkRuntimeConfigResult;
  onDismiss: () => void;
}) {
  return (
    <div className="border border-slate-800 bg-slate-950/60 rounded p-2 space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-300">
          Updated <span className="text-emerald-300">{result.succeeded}</span> /{' '}
          <span className="text-white">{result.total}</span> scrapers
          {result.failed.length > 0 && (
            <> · <span className="text-red-300">{result.failed.length} failed</span></>
          )}
        </span>
        <button onClick={onDismiss} className="text-slate-500 hover:text-white">dismiss</button>
      </div>
      {result.failed.length > 0 && (
        <ul className="text-[11px] text-red-300 space-y-0.5">
          {result.failed.map((f) => (
            <li key={f.id}><code>{f.id}</code>: {f.error}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
